import { format } from "date-fns"
import { UserResponseData } from "../apis/models/BrandlistResponse";
import { OfficeWorkFeeOption, OfficeWorkOption, OfficeWorkResponseData } from "../apis/models/OfficeWorkResponse";
import { ProcessFeeDetailsResponseData, ProcessFeeOption } from "../apis/models/ProcessFeeDetailsResponse";
import { DeliveryOptionResponseData, SizeOptionResponseData, WrappingFeeResponseData } from "../apis/models/WrappingFeeResponse";
import { CategorySpan, Choice, DateArrItem, DeliveryOption, OfficeWorkFeeItemFormatted, OfficeWorkFeeItemFormattedData, WrappingFeeDataItem, WrappingFeeFormattedDeliveryItem, WrappingFeeFormattedSizeItem, WrappingSizeOption } from "./types";
import readXlsxFile from "read-excel-file";

export const formatCurrency = (num: number) => {
  return `${formatNumber(num)}円`
}

export const formatNumber = (num: number, defaultValue: string = "0") => {
  if (isNaN(num) || num === 0) {
    return defaultValue;
  }
  return new Intl.NumberFormat('ja-JP').format(num)
}

export const str2number = (value: string | undefined) => {
  if (!value) {
    return 0
  }
  const numValue = parseInt(value)
  if (isNaN(numValue)) {
    return 0
  }
  return numValue
}

export const nowAsString = () => {
  return format(new Date(), "yyyy/MM/dd");
}

export const dateToString = (date: string | Date | null, f: string = "yyyy/MM/dd") => {
    if (!date) {
      return '';
    }
    const d = new Date(date)
    if(Number.isNaN(d.getTime())) {
      return date.toString();
    } else {
      return format(d, f);
    }
}

export const isInvalidDate = (date: string | Date) => {
  return Number.isNaN(new Date(date).getTime())
}

export const convertUserOptions = (userOptions: UserResponseData[]) => {
    const formatedOptions: Choice[] = [{ name: "　", value: ""}];
    userOptions.forEach(item => {
        const option = {
            name: item.userName,
            value: item.aid.toString()
        }
        formatedOptions.push(option);
    })
    return formatedOptions;
}

export const createMonthOptions = () => {
    const months = Array.from({length: 12}, (v,i) => {
        const item: Choice = {
            name: `${i+1}`,
            value: `${i+1}`
        }
        return item
    });
    months.unshift({
        name: "なし",
        value: "",
    })
    return months;
}

export const createDayOptions = (month: number) => {
    const thirtyday = [4,6,9,11];
    const length = month === 2 ? 29 : thirtyday.includes(month) ? 30 : 31;
    if (month > 0) {
        const days = Array.from({length: length}, (v,i) => {
            const item: Choice = {
                name: `${i+1}`,
                value: `${i+1}`
            }
            return item
        });
        days.unshift({
            name: "なし",
            value: "",
        })
        return days;
    } else return []
}

export const createBillingOptions = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const oldest = new Date(2022, 11 - 1, 1)
    const latest = new Date(currentYear, currentMonth + 1, 1) // next month is latest
    const options: Choice[] =[{name: "　", value: ""}]

    for (let i = 0; i < 120; i++) { // max 10 years
      const date = new Date(latest.getFullYear(), latest.getMonth() - i, 1)
      if (oldest.getTime() > date.getTime()) {
        break;
      }
      const data = `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}`;
      options.push({
        name: data,
        value: data
      });
    }
    return options;
}

export const toBase64 = (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

export const returnProcessFeeId = (options: ProcessFeeOption[], workdetails: string) => {
    const id = options.find((item) => item.processFeeOption.workDetails === workdetails)?.processFeeOptionId;
    if (!id || id === undefined) {return ""}
    return id
}

export const returnProcessFeeUp = (options: ProcessFeeOption[], processFeeOptionId: string) => {
    const up = options.find((item) => item.processFeeOption.processFeeOptionId === processFeeOptionId)?.processFeeOption.unitPrice;
    if (!up || up === undefined) {return ""}
    return up
}

export const returnProcessFeeWork = (options: ProcessFeeOption[], processFeeOptionId: string) => {
    const work = options.find((item) => item.processFeeOption.processFeeOptionId === processFeeOptionId)?.processFeeOption.workDetails;
    if (!work || work === undefined) {return ""}
    return work
}

export const returnProcessFeeTotalAmount = (input: ProcessFeeDetailsResponseData[]) => {
    let amount = 0;
    input.forEach(item => {
        amount += item.processFeeOption.unitPrice * item.quantity;
    })
    return amount;
}

export const formatOfficeWorkData = (options: OfficeWorkOption[], data: OfficeWorkResponseData[]) => {const dates: DateArrItem[] = [];
    const formattedDataArr: OfficeWorkFeeItemFormatted[] = [];
    const categories: CategorySpan[] = [];
    const findOrder = (id: string) => {
      const matchingItem = options.find(it => it.officeWorkFeeOptionId === id);
      if (matchingItem && matchingItem !== undefined) {
        if (matchingItem.order !== undefined) {
            return matchingItem.order
        } else {
            return 0
        }
      }
      return 0
    }
    //format office work data
    data.forEach((item, index) => {
        if (!dates.some(d => d.date === dateToString(item.dateAdded))) {
            dates.push({
                date: dateToString(item.dateAdded),
                remarks: item.memo
            });
        }
        const existingCategory = formattedDataArr.find(w => w.category === item.officeWorkFeeOption?.category);
        const existingCategoryid = formattedDataArr.findIndex(w => w.category === item.officeWorkFeeOption?.category);
        if (!existingCategory || existingCategory === undefined) {
            const initialItem:OfficeWorkFeeItemFormatted = {
                category: item.officeWorkFeeOption?.category ?? "",
                unitPrice: item.officeWorkFeeOption?.unitPrice ?? 0,
                order: findOrder(item.officeWorkFeeOptionId),
                workDetails: item.officeWorkFeeOption?.workDetails ?? "",
                officeWorkFeeOptionId: item.officeWorkFeeOptionId,
                data: [
                    {
                        dateAdded: dateToString(item.dateAdded),
                        quantity: item.quantity
                    }
                ]
            }
            formattedDataArr.push(initialItem);
        } else {
            if (existingCategory.workDetails !== item.officeWorkFeeOption?.workDetails) {
                const initialItem:OfficeWorkFeeItemFormatted = {
                    category: item.officeWorkFeeOption?.category ?? "",
                    unitPrice: item.officeWorkFeeOption?.unitPrice ?? 0,
                    workDetails: item.officeWorkFeeOption?.workDetails ?? "",
                    order: findOrder(item.officeWorkFeeOptionId),
                    officeWorkFeeOptionId: item.officeWorkFeeOptionId,
                    data: [
                        {
                            dateAdded: dateToString(item.dateAdded),
                            quantity: item.quantity
                        }
                    ]
                }
                formattedDataArr.push(initialItem)
            } else {
                const newItem = {...existingCategory};
                let quantity = item.quantity;
                const sameDateData = newItem.data.find(d => dateToString(d.dateAdded) === dateToString(item.dateAdded));
                const sameDateDataId = newItem.data.findIndex(d => dateToString(d.dateAdded) === dateToString(item.dateAdded));
                if (sameDateData && sameDateData !== undefined) {
                    quantity += sameDateData.quantity;
                    const newItemData = [...newItem.data];
                    newItemData.splice(sameDateDataId, 1, {
                        dateAdded: dateToString(item.dateAdded),
                        quantity: quantity
                    })
                    newItem.data = newItemData;
                } else {
                    newItem.data.push({
                        dateAdded: dateToString(item.dateAdded),
                        quantity: quantity
                    })
                }
                formattedDataArr.splice(existingCategoryid, 1, newItem);
            }
        }
    })
    //sort dates
    dates.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    //check if all categories were in data even if no qty
    options.forEach(opt => {
      if (formattedDataArr.every(item => item.officeWorkFeeOptionId !== opt.officeWorkFeeOptionId)) {
        const item: OfficeWorkFeeItemFormatted = {
          category: opt.officeWorkFeeOption.category,
          officeWorkFeeOptionId: opt.officeWorkFeeOptionId,
          order: opt.order !== undefined ? opt.order : 0,
          unitPrice: opt.officeWorkFeeOption.unitPrice,
          workDetails: opt.officeWorkFeeOption.workDetails,
          data: []
        }
        formattedDataArr.push(item);
      }
    })
    //sort data
    formattedDataArr.sort((a,b) => a.order - b.order);
    //create category column span to display
    let prevInd = 0;
    formattedDataArr.forEach((it, index) => {
       if (categories.length === 0) {
        const cat: CategorySpan = {
            name: it.category,
            span: 1,
        }
        categories.push(cat);
       } else {
        const cat: CategorySpan = {
            name: it.category,
            span: 1,
        }
        if (cat.name !== categories[prevInd].name) {
            categories.push(cat);
            prevInd += 1;
        } else {
            cat.span = categories[prevInd].span + 1;
            categories.splice(prevInd, 1, cat);
        }
       }
    });
    //set data to state
    return {categories, formattedDataArr, dates}
}

export const returnOfficeWorkOptionDetails = (options: OfficeWorkOption[], optionId: string) => {
    const optionDetails = options.find(item => item.officeWorkFeeOptionId === optionId)?.officeWorkFeeOption;
    if (!optionDetails || optionDetails === undefined) {
        const empty: OfficeWorkFeeOption = {
            category: "",
            created: "",
            modified: "",
            officeWorkFeeOptionId: "",
            unitPrice: 0,
            workDetails: ""
        }
        return empty;
    }
    return optionDetails;
} 

export const fromValToName = (options: Choice[], val: any) => {
    const obj = options.find(item => item.value === val);
    if (!obj || obj === undefined) {
        return ""
    }
    return obj.name.toString();
}

export const disableInvOutrangeDate = (startDate: Date, endDate: Date, d: Date) => {
    const disabled = d.getTime() < startDate.getTime() || d.getTime() > endDate.getTime();
    return disabled;
}

export const disableInvOutrangeDateExtra = (startDate: Date, endDate: Date, d: Date, dArr: DateArrItem[]) => {
    const newDateArr =  dArr.map(dayItem => dayItem.date);
    let disabled = d.getTime() < startDate.getTime() || d.getTime() > endDate.getTime() || newDateArr.some(item => item === dateToString(d));
    return disabled;
}

export const returnOfficeWorkQty = (d: string, data: OfficeWorkFeeItemFormattedData[]) => {
    const matchingDate = data.find(it => it.dateAdded === d);
    if (matchingDate && matchingDate !== undefined) {
        return matchingDate.quantity;
    }
    return ""
}

export const calculateTotalItem = (input: OfficeWorkFeeItemFormattedData[]) => {
    const total = input.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0);
    return total;
}

export const calculateTotalAmount = (input: OfficeWorkFeeItemFormattedData[], price: number) => {
    const total = input.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0);
    const amount = total * price;
    return amount;
}

export const returnDeliveryOrder = (id: string, options: DeliveryOption[]) => {
    const selectOptionId = options.findIndex(item => item.deliveryMethodOptionId === id);
    if (selectOptionId >=0) {
        return options[selectOptionId].order
    }
    return 0
}

export const returnDeliveryDefaultFlag = (id: string, options: DeliveryOption[]) => {
    const selectOptionId = options.findIndex(item => item.deliveryMethodOptionId === id);
    if (selectOptionId >=0) {
        return options[selectOptionId].defaultFlag
    }
    return 0
}

export const returnSizeOrder = (id: string, options: WrappingSizeOption[]) => {
    const selectOptionId = options.findIndex(item => item.wrappingSizeOptionId === id);
    if (selectOptionId >=0) {
        return options[selectOptionId].order
    }
    return 0
}

export const calculateTotalWrappingAmount = (input: WrappingFeeDataItem[], price: number) => {
    const total = input.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0);
    const amount = total * price;
    return amount;
}

export const calculateTotalWrappingQty = (input: WrappingFeeDataItem[]) => {
    const total = input.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0);
    return total;
}

export const calculateTotalWrappingDis = (input: DateArrItem[]) => {
    const total = input.reduce((previousValue, currentValue) => previousValue + Number(currentValue.differenceInSalesNumber!), 0);
    return total;
}

export const returnWrappingQty = (d: string, data: WrappingFeeDataItem[]) => {
    const matchingDate = data.find(it => it.dateAdded === d);
    if (matchingDate && matchingDate !== undefined) {
        return matchingDate.quantity;
    }
    return ""
}

export const returnWrappingSizeRowTotal = (d: string, data: WrappingFeeFormattedSizeItem[]) => {
    let total = 0;
    data.forEach(item => {
        item.data.forEach(it => {
            if (it.dateAdded === d) {
                if (it.quantity >= 0) {
                    total += it.quantity
                }
            }
        })
    })
    return total
}

export const returnWrappingDeliveryRowTotal = (d: string, data: WrappingFeeFormattedDeliveryItem[], differenceInSalesNumber: number) => {
    let total = 0;
    data.forEach(item => {
        item.data.forEach(it => {
            if (it.dateAdded === d) {
                if (it.quantity >= 0) {
                    total += it.quantity
                }
            }
        })
    })
    return total + Number(differenceInSalesNumber)
}

export const excelFileReader = (file: File) => new Promise((resolve, reject) => {
    readXlsxFile(file).then(rows => {
        resolve(rows)
      }).catch(err => {
        reject(err)
      })
});

export const formatWrappingFeeData = (sizes: SizeOptionResponseData[], deliveries: DeliveryOptionResponseData[], details: WrappingFeeResponseData[]) => {
    const deliveryData: WrappingFeeFormattedDeliveryItem[] = [];
    const sizeData: WrappingFeeFormattedSizeItem[] = [];
    const dateArray: DateArrItem[] = [];
    const deliveryOptions = [...deliveries];
    const sizeOptions = [...sizes];
    details.forEach((detail, index) => {
      if (detail.deliveryMethodOptionId && detail.deliveryMethodOption) {
        //format delivery data
        const deliveryDataItem: WrappingFeeFormattedDeliveryItem = {
          deliveryMethodOptionId: detail.deliveryMethodOptionId,
          item: detail.deliveryMethodOption.deliveryMethod,
          order: returnDeliveryOrder(detail.deliveryMethodOptionId, deliveryOptions),
          unitPrice: detail.deliveryMethodOption.deliveryMethodUnitPrice,
          defaultFlag: returnDeliveryDefaultFlag(detail.deliveryMethodOptionId, deliveryOptions),
          data: [
            {
              dateAdded: dateToString(detail.dateAdded),
              quantity: detail.quantity
            }
          ]
        }
        if (deliveryData.length === 0) {
          deliveryData.push(deliveryDataItem);
        } else {
          const existingDataItemIndex = deliveryData.findIndex(item => item.deliveryMethodOptionId === detail.deliveryMethodOptionId);
          if (existingDataItemIndex >= 0) {
            const copyExistingDataItem = {...deliveryData[existingDataItemIndex]};
            const copyExisitngDataItemData = [...copyExistingDataItem.data];
            if (copyExisitngDataItemData.every(item => item.dateAdded !== dateToString(detail.dateAdded))) {
              copyExisitngDataItemData.push({
                dateAdded: dateToString(detail.dateAdded),
                quantity: detail.quantity
              })
            } else {
              const existingItemIndexInData = copyExisitngDataItemData.findIndex(it => it.dateAdded === dateToString(detail.dateAdded));
              const copyExistingItemInData = copyExisitngDataItemData[existingItemIndexInData];
              copyExistingItemInData.quantity += detail.quantity;
              copyExisitngDataItemData.splice(existingItemIndexInData, 1, copyExistingItemInData);
            }
            copyExistingDataItem.data = copyExisitngDataItemData;
            deliveryData.splice(existingDataItemIndex, 1, copyExistingDataItem)
          } else {
            deliveryData.push(deliveryDataItem);
          }
        }
      }
      //format size data
      if (detail.wrappingSizeOptionId && detail.wrappingSizeOption) {
        const sizeDataItem: WrappingFeeFormattedSizeItem = {
          wrappingSizeOptionId: detail.wrappingSizeOptionId,
          item: detail.wrappingSizeOption.wrappingSize,
          order: returnSizeOrder(detail.wrappingSizeOptionId, sizeOptions),
          unitPrice: detail.wrappingSizeOption.wrappingSizeUnitPrice,
          data: [
            {
              dateAdded: dateToString(detail.dateAdded),
              quantity: detail.quantity
            }
          ]
        }
        if (sizeData.length === 0) {
          sizeData.push(sizeDataItem);
        } else {
          const existingDataItemIndex = sizeData.findIndex(item => item.wrappingSizeOptionId === detail.wrappingSizeOptionId);
          if (existingDataItemIndex >= 0) {
            const copyExistingDataItem = {...sizeData[existingDataItemIndex]};
            const copyExisitngDataItemData = [...copyExistingDataItem.data];
            if (copyExisitngDataItemData.every(item => item.dateAdded !== dateToString(detail.dateAdded))) {
              copyExisitngDataItemData.push({
                dateAdded: dateToString(detail.dateAdded),
                quantity: detail.quantity
              })
            } else {
              const existingItemIndexInData = copyExisitngDataItemData.findIndex(it => it.dateAdded === dateToString(detail.dateAdded));
              const copyExistingItemInData = copyExisitngDataItemData[existingItemIndexInData];
              copyExistingItemInData.quantity += detail.quantity;
              copyExisitngDataItemData.splice(existingItemIndexInData, 1, copyExistingItemInData);
            }
            copyExistingDataItem.data = copyExisitngDataItemData;
            sizeData.splice(existingDataItemIndex, 1, copyExistingDataItem)
          } else {
            sizeData.push(sizeDataItem);
          }
        }
      }
      //format date array
      const dateItem: DateArrItem = {
        date: dateToString(detail.dateAdded),
        remarks: detail.memo
      }
      if (dateArray.every(item => item.date !== dateItem.date)) {
        dateArray.push(dateItem)
      }
    })
    //sort date
    dateArray.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    //check if all delivery items were there to render even if no quantity
    if (deliveryData.length < deliveryOptions.length) {
      deliveryOptions.forEach(option => {
        if ( deliveryData.every(item => item.deliveryMethodOptionId !== option.deliveryMethodOptionId)) {
          const deliveryDataItem: WrappingFeeFormattedDeliveryItem = {
            deliveryMethodOptionId: option.deliveryMethodOptionId!,
            item: option.deliveryMethodOption.deliveryMethod,
            order: option.order,
            unitPrice: option.deliveryMethodOption.deliveryMethodUnitPrice,
            defaultFlag: returnDeliveryDefaultFlag(option.deliveryMethodOptionId, deliveryOptions),
            data: []
          }
          deliveryData.push(deliveryDataItem);
        }
      })
    }
    //sort delivery data
    deliveryData.sort((a,b) => a.order - b.order);
    //check if all size items were there to render even if no quantity
    if (sizeData.length < sizeOptions.length) {
      sizeOptions.forEach(option => {
        if (sizeData.every(item => item.wrappingSizeOptionId !== option.wrappingSizeOptionId)) {
          const sizeDataItem: WrappingFeeFormattedSizeItem = {
            wrappingSizeOptionId: option.wrappingSizeOptionId!,
            item: option.wrappingSizeOption.wrappingSize,
            order: option.order!,
            unitPrice: option.wrappingSizeOption.wrappingSizeUnitPrice,
            data: []
          }
          sizeData.push(sizeDataItem);
        }
      })
    }
    //sort size data
    sizeData.sort((a,b) => a.order! - b.order!)
    //set data to state
    return {dateArray, sizeData, deliveryData}
}

export const chunkArray = (arr: string[], perChunk: number = 1000) => {
    return arr.reduce((resultArray: string[][], item: string, index) => { 
        const chunkIndex = Math.floor(index/perChunk)
      
        if(!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = [] // start a new chunk
        }
      
        resultArray[chunkIndex].push(item)
      
        return resultArray
      }, [])
}

const wait = (ms: number) => new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms);
});

export const retryWithDelay = async <T>(fn: () => Promise<T>, retries: number = 1, interval: number = 50): Promise<T> => {
    try {
        return await fn();
    } catch (err) {
        // if no retries left, throw error
        if (retries <= 0) {
            return Promise.reject(err)
        }

        await wait(interval);

        // recurse with decremented retry
        console.log('Retrying...')
        return retryWithDelay(fn, retries-1, interval)
    }
}